import type { ExtendedMedicationRequest } from '@seen/core/models/medicationRequest'
import { Button } from '@seen/ui'
import { IconReload, IconSignature } from '@tabler/icons-react'
import type { JSX } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#src/components/core/index.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { getMedicationThreadId } from '#src/hooks/useMedicationDraftsByThread.ts'

export function MedicationElationDraftActions({
  medication,
  onDraftPrescription,
  onOpenDraftPrescription,
  draftIdsByThreadId,
  draftingMedicationIds,
}: {
  medication: ExtendedMedicationRequest
  onDraftPrescription?: (
    medication: ExtendedMedicationRequest,
    orderType: 'Refill'
  ) => void | Promise<number | undefined>
  onOpenDraftPrescription?: (
    draftId: number | string,
    medication: ExtendedMedicationRequest
  ) => void
  draftIdsByThreadId?: ReadonlyMap<string, number | string>
  draftingMedicationIds?: ReadonlySet<string>
}): JSX.Element | null {
  const isReadOnly = useIsReadOnly()
  const isElationSourced = !!medication.externalResourceJSON
  const refillReason = medication.refillUnavailableReason
  const threadId = getMedicationThreadId(medication)
  const draftId = threadId ? draftIdsByThreadId?.get(threadId) : undefined
  const isCreatingDraft = draftingMedicationIds?.has(medication.id) ?? false
  const canSignDraft =
    !isReadOnly &&
    onOpenDraftPrescription !== undefined &&
    draftId !== undefined
  const canDraft =
    !isReadOnly && !!onDraftPrescription && isElationSourced && !refillReason

  if (canSignDraft) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="xs"
            className="text-muted-foreground hover:text-foreground [&_svg]:size-3.5"
            onClick={() => onOpenDraftPrescription(draftId, medication)}
          >
            <IconSignature />
            Sign
          </Button>
        </TooltipTrigger>
        <TooltipContent>Open draft in Elation</TooltipContent>
      </Tooltip>
    )
  }

  if (isReadOnly || !onDraftPrescription || !isElationSourced) {
    return null
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>
          <Button
            variant="ghost"
            size="xs"
            className="text-muted-foreground hover:text-foreground [&_svg]:size-3.5"
            loading={isCreatingDraft}
            disabled={!canDraft || isCreatingDraft}
            onClick={() => {
              void onDraftPrescription(medication, 'Refill')
            }}
          >
            <IconReload />
            Refill
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent>
        {refillReason ?? 'Create refill draft in Elation'}
      </TooltipContent>
    </Tooltip>
  )
}
